import { Box, Typography, Slider, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, Control, FieldValues, Path, useWatch } from 'react-hook-form';

interface DistanceSliderProps<T extends FieldValues> {
  readonly control: Control<T>;
  readonly enabledFieldName: Path<T>;
  readonly distanceFieldName: Path<T>;
  readonly label?: string;
}

/**
 * Reusable distance slider component with an optional enable toggle.
 *
 * Features:
 * - Checkbox toggle to enable/disable the slider
 * - Slider from 0 to 50 km with marks at 0, 25, 50
 * - Real-time value label display
 * - Generic over any react-hook-form FieldValues type
 * - Grayed out when disabled
 *
 * @example
 * ```tsx
 * <DistanceSlider
 *   control={control}
 *   enabledFieldName="distanceEnabled"
 *   distanceFieldName="maxDistance"
 *   label="Distance"
 * />
 * ```
 */
export function DistanceSlider<T extends FieldValues>({
  control,
  enabledFieldName,
  distanceFieldName,
  label,
}: DistanceSliderProps<T>) {
  const enabled = useWatch({ control, name: enabledFieldName }) as boolean;

  return (
    <Box>
      <Controller
        name={enabledFieldName}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={!!field.value}
                data-testid="distance-slider-toggle"
                sx={{
                  color: 'primary.main',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              label ? (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {label}
                </Typography>
              ) : null
            }
          />
        )}
      />

      <Box sx={{ px: 2, mt: 2 }}>
        <Controller
          name={distanceFieldName}
          control={control}
          render={({ field }) => (
            <Slider
              {...field}
              disabled={!enabled}
              min={0}
              max={50}
              marks={[
                { value: 0, label: '0 km' },
                { value: 25, label: '25 km' },
                { value: 50, label: '50 km' },
              ]}
              valueLabelDisplay="on"
              valueLabelFormat={value => `${value} km`}
              data-testid="distance-slider-input"
              sx={{
                color: enabled ? 'primary.main' : 'grey.400',
                '& .MuiSlider-thumb': {
                  backgroundColor: enabled ? 'primary.main' : 'grey.400',
                },
                '& .MuiSlider-track': {
                  backgroundColor: enabled ? 'primary.main' : 'grey.400',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'grey.300',
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
}
